import { useMutation } from '@apollo/client';
import { REMOVE_TESTS } from '../../apollo/tests.ts';
import { useCallback, useState } from 'react';
import {
  CONTINUE_TEST_ATTEMPT,
  CREATE_TEST_ATTEMPT,
} from '../../apollo/test-attempt.ts';
import {
  TestAttemptMode,
  TestStatus,
} from '../../apollo/__generated__/graphql.ts';
import { useNavigate } from 'react-router-dom';
import {
  GET_MY_TEST_ATTEMPTS,
  GET_USER_COURSE,
} from '../../apollo/user-assignments.ts';
import { GET_TESTS } from '../../apollo/tests.ts';

export const useRemoveTests = (afterRemove?: () => void) => {
  const [isModalOpen, setModal] = useState(false);
  const [remove] = useMutation(REMOVE_TESTS);
  const onRemoveTests = (ids: number[]) => {
    if (!ids.length) return null;

    afterRemove?.();

    return remove({
      awaitRefetchQueries: true,
      refetchQueries: [GET_TESTS],
      variables: {
        input: {
          ids,
        },
      },
    });
  };

  const onCloseModal = () => setModal(false);
  const onOpenModal = () => setModal(true);

  return {
    onRemoveTests,
    onCloseModal,
    onOpenModal,
    isModalOpen,
  };
};

export const useTestsSearch = () => {
  const [filter, setFilter] = useState<{
    name?: string;
    description?: string;
    notes?: string;
    typeId?: number;
    status?: TestStatus;
  }>({
    name: undefined,
    description: undefined,
    notes: undefined,
    typeId: undefined,
    status: undefined,
  });
  const onChangeFilter = useCallback((value?: string, type?: string) => {
    setFilter((prev) => {
      const next = {
        name: type === 'name' ? value : undefined,
        description: type === 'description' ? value : undefined,
        notes: type === 'notes' ? value : undefined,
        typeId: type === 'typeId' && value ? +value : undefined,
        status: type === 'status' ? (value as TestStatus | undefined) : undefined,
      };

      if (
        prev.name === next.name &&
        prev.description === next.description &&
        prev.notes === next.notes &&
        prev.typeId === next.typeId &&
        prev.status === next.status
      ) {
        return prev;
      }

      return next;
    });
  }, []);

  return { filter, onChangeFilter };
};

export const useStartNewTest = () => {
  const navigate = useNavigate();
  const [create, { loading }] = useMutation(CREATE_TEST_ATTEMPT, {
    refetchQueries: [GET_MY_TEST_ATTEMPTS, GET_USER_COURSE],
  });

  const startNewTest = (testId: number) => {
    create({
      variables: {
        input: { mode: TestAttemptMode.Practice, testId },
      },
    }).then((res) =>
      navigate(`/test-attempt/${res.data?.testAttempt.create.id}`),
    );
  };

  return { startNewTest, loading };
};

export const useContinueTest = (testAttemptId?: number) => {
  const navigate = useNavigate();

  const [continueTestAttempt] = useMutation(CONTINUE_TEST_ATTEMPT);

  return () => {
    if (testAttemptId) {
      continueTestAttempt({
        variables: {
          input: { id: testAttemptId },
        },
      }).then((res) =>
        navigate(`/test-attempt/${res.data?.testAttempt.continue.id}`),
      );
    }
  };
};
